// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="PhoneInput" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Country data ─────────────────────────────────────────────────────────────

export interface PhoneCountry {
  code: string; // ISO 3166-1 alpha-2
  dial: string; // e.g., '+1'
  flag: string; // emoji
  name: string;
}

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: 'US', dial: '+1', flag: '🇺🇸', name: 'United States' },
  { code: 'CA', dial: '+1', flag: '🇨🇦', name: 'Canada' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'AU', dial: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: 'BR', dial: '+55', flag: '🇧🇷', name: 'Brazil' },
  { code: 'DE', dial: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: 'FR', dial: '+33', flag: '🇫🇷', name: 'France' },
  { code: 'IN', dial: '+91', flag: '🇮🇳', name: 'India' },
  { code: 'MX', dial: '+52', flag: '🇲🇽', name: 'Mexico' },
  { code: 'JP', dial: '+81', flag: '🇯🇵', name: 'Japan' },
];

// ─── Value type ───────────────────────────────────────────────────────────────

export interface PhoneInputValue {
  country: string; // e.g., 'US'
  number: string; // e.g., '5551234567'
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PhoneInputProps {
  value?: PhoneInputValue;
  onChange?: (value: PhoneInputValue) => void;
  defaultCountry?: string;
  countries?: PhoneCountry[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Compound phone input — a compact country-dial-code selector on the left and
 * a number input on the right, sharing a single visual border so they appear
 * as one unified field.
 *
 * The value is a typed object `{ country: string; number: string }` where
 * `country` is the ISO 3166-1 alpha-2 code (e.g. `'US'`) and `number` is the
 * raw number string.
 *
 * The country selector uses an invisible native `<select>` overlaid on a styled
 * display span — this gives us full browser-native dropdown behaviour while
 * keeping the visual consistent with the design system.
 *
 * @example
 * // Uncontrolled (defaultCountry only)
 * <PhoneInput onChange={(v) => console.log(v)} />
 *
 * @example
 * // Controlled
 * const [phone, setPhone] = React.useState<PhoneInputValue>({ country: 'BR', number: '' });
 * <PhoneInput value={phone} onChange={setPhone} />
 *
 * @example
 * // Inside FieldWrapper
 * <FieldWrapper label="Mobile Number" hint="Include country code">
 *   <PhoneInput defaultCountry="US" />
 * </FieldWrapper>
 *
 * @example
 * // React Hook Form
 * <FormField
 *   control={form.control}
 *   name="mobile"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Mobile</FormLabel>
 *       <FormControl>
 *         <PhoneInput value={field.value} onChange={field.onChange} />
 *       </FormControl>
 *       <FormMessage />
 *     </FormItem>
 *   )}
 * />
 */
const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      defaultCountry = 'US',
      countries = PHONE_COUNTRIES,
      placeholder = '555 000 0000',
      disabled,
      className,
    },
    ref,
  ) => {
    const [country, setCountry] = React.useState(value?.country ?? defaultCountry);
    const [number, setNumber] = React.useState(value?.number ?? '');

    // Sync controlled value in
    React.useEffect(() => {
      if (value) {
        setCountry(value.country);
        setNumber(value.number);
      }
    }, [value?.country, value?.number]);

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setCountry(e.target.value);
      onChange?.({ country: e.target.value, number });
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setNumber(e.target.value);
      onChange?.({ country, number: e.target.value });
    };

    const selected = countries.find((c) => c.code === country) ?? countries[0];

    return (
      <div
        className={cn(
          'flex h-9 w-full overflow-hidden rounded-[var(--field-radius,var(--radius))]',
          'border border-input bg-input-bg shadow-sm',
          'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background',
          'aria-[invalid=true]:border-destructive',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        {/* Country selector — invisible native select overlaid on styled display */}
        <div className="relative flex shrink-0 items-center border-r border-input">
          {/* Visual display — flag + dial + chevron */}
          <span
            className="pointer-events-none flex items-center gap-1 px-3 text-sm text-foreground"
            aria-hidden="true"
          >
            <span>{selected.flag}</span>
            <span className="font-medium">{selected.dial}</span>
            <span className="text-muted-foreground text-xs">▾</span>
          </span>

          {/* Invisible native select on top — provides the interactive dropdown */}
          <select
            value={country}
            onChange={handleCountryChange}
            disabled={disabled}
            aria-label="Country code"
            className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
          >
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.dial} — {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Number input */}
        <input
          ref={ref}
          type="tel"
          value={number}
          onChange={handleNumberChange}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Phone number"
          className={cn(
            'min-w-0 flex-1 bg-transparent px-3 text-base sm:text-sm text-foreground',
            'placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed',
          )}
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { PhoneInput };
